import { chordTones, type ChordTone } from '../chord/chord-tones.js'
import { fromName } from '../chord/library.js'

/**
 * How a chord sounds under a melody (melody toolkit plan M2 step 2.2; spec §5.1). Bowie's rule, in order:
 *
 * 1. Bass: the root, or the slash bass, in C2–B2. When it is not at least a minor 3rd below the melody's
 *    lowest note in the chord's span, an octave lower, though never below E1.
 * 2. Up to three upper notes by the chord family's priority, close-stacked in C3–B3, the top note at least
 *    a minor 3rd below the melody's lowest note. Of the stackings that fit, the one with the highest top.
 * 3. When none fits, the lowest-priority note goes and it tries again. 3rds and 7ths never go below C3.
 * 4. With no upper note left, the 5th alone, allowed down to G2: a power chord. Otherwise the bass alone.
 *
 * Velocity and restriking belong to the schedule. An unknown symbol is silence, never an error.
 */

export interface ChordVoicing {
  /** MIDI */
  bass: number
  /** MIDI, low to high */
  upper: number[]
}

const bassLow = 36
const lowestBass = 28
const upperLow = 48
const upperHigh = 59
const powerFifthLow = 43
/** A minor 3rd: the space kept between the chords and the melody */
const clearance = 3

const mod12 = (value: number) => ((value % 12) + 12) % 12

const interval = (tone: ChordTone, rootPitch: number) => mod12(tone.pitchClass - rootPitch)

/** The upper notes in priority order, highest first: Bowie's table by chord family */
const priorityOf = (tones: ChordTone[]): ChordTone[] => {
  const root = tones.find(tone => tone.degree === 1)
  if (!root) {
    return []
  }
  const at = (degree: number) => tones.find(tone => tone.degree === degree)
  const [third, fourth, fifth, sixth, seventh, second] = [at(3), at(4), at(5), at(6), at(7), at(2)]
  const pick = (...chosen: (ChordTone | undefined)[]) => chosen.filter(tone => tone !== undefined)
  if (!third) {
    const suspended = fourth ?? second
    if (suspended) {
      return seventh ? pick(suspended, seventh, fifth) : pick(suspended, fifth, root)
    }
    /** A power chord: never a 3rd */
    return pick(fifth, root)
  }
  if (!seventh) {
    return sixth ? pick(third, sixth, fifth) : second ? pick(third, second, fifth) : pick(third, fifth, root)
  }
  const isDominant = interval(third, root.pitchClass) === 4 && interval(seventh, root.pitchClass) === 10
  const alteration = isDominant
    ? tones.find(tone => {
        const semitones = interval(tone, root.pitchClass)
        return (
          (tone.degree === 2 && (semitones === 1 || semitones === 3)) ||
          (tone.degree === 5 && (semitones === 6 || semitones === 8)) ||
          (tone.degree === 6 && semitones === 8)
        )
      })
    : undefined
  if (alteration) {
    return pick(third, seventh, alteration)
  }
  if (sixth) {
    return pick(third, seventh, sixth)
  }
  if (fourth) {
    return interval(third, root.pitchClass) === 3 ? pick(third, seventh, fourth) : pick(fourth, seventh, second)
  }
  return second ? pick(third, seventh, second) : pick(third, seventh, fifth)
}

/**
 * The close stacking of the pitch classes `pitches` that starts on `start`, its lowest note at C3 or just
 * above: each next note the nearest one above the last
 */
const stackFrom = (start: number, pitches: number[]): number[] => {
  const first = upperLow + mod12(start - upperLow)
  const rest = pitches.filter(pitch => pitch !== start).sort((a, b) => mod12(a - start) - mod12(b - start))
  const stack = [first]
  for (const pitch of rest) {
    const last = stack[stack.length - 1] ?? first
    stack.push(last + mod12(pitch - last))
  }
  return stack
}

/** Of the close stackings, the one with the highest top note that fits under `ceiling`, or none */
const bestStacking = (pitches: number[], ceiling: number): number[] | undefined =>
  pitches
    .map(start => stackFrom(start, pitches))
    .filter(stack => (stack[stack.length - 1] ?? Infinity) <= ceiling)
    .sort((a, b) => (b[b.length - 1] ?? 0) - (a[a.length - 1] ?? 0))[0]

export interface VoiceTonesInput {
  tones: ChordTone[]
  /** The slash bass's pitch class, when there is one */
  bassPitch?: number
}

/**
 * The voicing of a chord given as its tones. `melodyLow` is the lowest melody note sounding in the chord's
 * span, at its sounding pitch; undefined when none does (`Chords only`, or an empty melody).
 */
export const voiceTones = (
  { tones, bassPitch }: VoiceTonesInput,
  melodyLow: number | undefined
): ChordVoicing | null => {
  const root = tones.find(tone => tone.degree === 1)
  if (!root) {
    return null
  }
  const bassClass = bassPitch ?? root.pitchClass
  const lowBass = bassLow + bassClass
  const isTooClose = melodyLow !== undefined && lowBass > melodyLow - clearance
  const bass = isTooClose && lowBass - 12 >= lowestBass ? lowBass - 12 : lowBass

  const ceiling = Math.min(upperHigh, melodyLow === undefined ? upperHigh : melodyLow - clearance)
  /** A slash bass that is a chord tone is not doubled above it (C/E: G and C) */
  const priority = priorityOf(tones).filter(tone => bassPitch === undefined || tone.pitchClass !== bassPitch)
  for (let count = priority.length; count > 0; count--) {
    const stack = bestStacking(
      priority.slice(0, count).map(tone => tone.pitchClass),
      ceiling
    )
    if (stack) {
      return { bass, upper: stack }
    }
  }
  /** Low 3rds turn to mud; a low 5th does not */
  const fifth = tones.find(tone => tone.degree === 5)
  if (fifth) {
    const highest = ceiling - mod12(ceiling - fifth.pitchClass)
    if (highest >= powerFifthLow) {
      return { bass, upper: [highest] }
    }
  }
  return { bass, upper: [] }
}

const naturals: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }
const accidentals: Record<string, number> = { '': 0, '#': 1, b: -1 }

/** A note name's pitch class, "Bb" → 10; undefined for anything else */
export const bassPitchClass = (name: string): number | undefined => {
  const natural = naturals[name[0] ?? '']
  const accidental = accidentals[name.slice(1)]
  return natural === undefined || accidental === undefined ? undefined : mod12(natural + accidental)
}

/** The voicing of a chord symbol, or null for one the chord library does not know: silence for its span */
export const voiceChord = (symbol: string, melodyLow: number | undefined): ChordVoicing | null => {
  const tones = chordTones(symbol)
  if (!tones) {
    return null
  }
  const bassNote = fromName(symbol)?.bassNote
  const bassPitch = bassNote === undefined ? undefined : bassPitchClass(bassNote)
  return voiceTones({ tones, ...(bassPitch === undefined ? {} : { bassPitch }) }, melodyLow)
}
